import { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { Sheet } from './Sheet';

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  label?: string;
  // Shown on the closed button when nothing is selected (an empty
  // `values` array means "no filter applied" everywhere this is used,
  // same convention the old single-select ALL_SIGNALS sentinel followed).
  placeholder: string;
  options: MultiSelectOption[];
  values: string[];
  onChange: (values: string[]) => void;
}

// A checklist-in-a-Sheet multi-select, not a native `<select multiple>` -
// that renders as a multi-line listbox with no visible checked state on
// most platforms, unusable on mobile. Reuses Sheet.tsx, the same
// bottom-sheet-with-a-button-list pattern AvatarSymbolPicker already
// uses, just toggling instead of closing on each pick.
export function MultiSelect({ label, placeholder, options, values, onChange }: MultiSelectProps) {
  const [open, setOpen] = useState(false);

  const toggle = (value: string) => {
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);
  };

  const selectedLabels = options.filter((o) => values.includes(o.value))?.map((o) => o.label);
  const summary =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length <= 2
        ? selectedLabels.join(', ')
        : `${selectedLabels.length} selected`;

  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && <span className="text-sm font-medium text-black/70 dark:text-white/70">{label}</span>}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative w-full text-left rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] pr-9 truncate outline-none focus:border-blue-500"
      >
        {summary}
        <ChevronDown
          size={16}
          className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-black/40 dark:text-white/40"
        />
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} title={label ?? placeholder}>
        <div className="flex flex-col gap-1">
          {values.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="self-start text-xs text-blue-500 px-3 pb-2"
            >
              Clear selection
            </button>
          )}
          {options.map((o) => {
            const checked = values.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggle(o.value)}
                className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-black/5 dark:hover:bg-white/10 transition-colors text-left"
              >
                <span className="text-[15px] truncate">{o.label}</span>
                <span
                  className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
                    checked ? 'bg-blue-500 border-blue-500' : 'border-black/20 dark:border-white/20'
                  }`}
                >
                  {checked && <Check size={14} color="white" strokeWidth={3} />}
                </span>
              </button>
            );
          })}
        </div>
      </Sheet>
    </div>
  );
}
